'use client';

// Scroll area built on Base UI — shape-system scrollbar, native overflow
// fallback on touch-primary devices. Scrollbar machinery adapted from Lina
// by SameerJS6 (https://lina.sameer.sh). WaPharma port under src/fluid
// (PHI-327); compose via props — keep API aligned with Fluid Functionalism.

import {
  createContext,
  forwardRef,
  useContext,
  type ComponentPropsWithoutRef,
  type ComponentRef,
  type HTMLAttributes,
  type Ref,
} from 'react';
import { ScrollArea as ScrollAreaPrimitive } from '@base-ui/react/scroll-area';
import { cn } from './cn.js';
import { useShape } from './shape-context.js';
import { useTouchPrimary } from './use-touch-primary.js';

const ScrollAreaContext = createContext<boolean>(false);

type Orientation = 'vertical' | 'horizontal' | 'both';

export interface FluidScrollAreaProps extends ComponentPropsWithoutRef<'div'> {
  viewportClassName?: string;
  /** Which axes get scrollbars. Defaults to `"vertical"`. */
  orientation?: Orientation;
  /** Extra props for the scrolling viewport (role, aria-*, data-*). */
  viewportProps?: HTMLAttributes<HTMLDivElement>;
  /** Ref for the scrolling viewport (DataTable virtualizer). */
  viewportRef?: Ref<HTMLDivElement>;
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (!ref) return;
  if (typeof ref === 'function') ref(value);
  else ref.current = value;
}

const FluidScrollArea = forwardRef<
  ComponentRef<typeof ScrollAreaPrimitive.Root>,
  FluidScrollAreaProps
>(function FluidScrollArea(
  {
    className,
    children,
    viewportClassName,
    orientation = 'vertical',
    viewportProps,
    viewportRef,
    ...props
  },
  ref,
) {
  const isTouch = useTouchPrimary();
  const { className: viewportExtraClass, ...viewportRest } = viewportProps ?? {};

  const viewportClass = cn(
    'size-full rounded-[inherit]',
    isTouch && orientation === 'vertical' && 'overflow-y-auto',
    isTouch && orientation === 'horizontal' && 'overflow-x-auto',
    isTouch && orientation === 'both' && 'overflow-auto',
    viewportClassName,
    viewportExtraClass,
  );

  return (
    <ScrollAreaContext.Provider value={isTouch}>
      {isTouch ? (
        <div
          ref={ref as Ref<HTMLDivElement>}
          role="group"
          data-slot="scroll-area"
          aria-roledescription="scroll area"
          className={cn('relative overflow-hidden', className)}
          {...props}
        >
          <div
            ref={(node) => assignRef(viewportRef, node)}
            data-slot="scroll-area-viewport"
            className={viewportClass}
            tabIndex={0}
            {...viewportRest}
          >
            {children}
          </div>
        </div>
      ) : (
        <ScrollAreaPrimitive.Root
          ref={ref}
          data-slot="scroll-area"
          className={cn('relative overflow-hidden', className)}
          {...props}
        >
          <ScrollAreaPrimitive.Viewport
            ref={(node) => assignRef(viewportRef, node as HTMLDivElement | null)}
            data-slot="scroll-area-viewport"
            className={viewportClass}
            {...viewportRest}
          >
            <ScrollAreaPrimitive.Content>{children}</ScrollAreaPrimitive.Content>
          </ScrollAreaPrimitive.Viewport>
          {orientation !== 'horizontal' && <ScrollBar orientation="vertical" />}
          {orientation !== 'vertical' && <ScrollBar orientation="horizontal" />}
          {orientation === 'both' && <ScrollAreaPrimitive.Corner />}
        </ScrollAreaPrimitive.Root>
      )}
    </ScrollAreaContext.Provider>
  );
});

FluidScrollArea.displayName = 'FluidScrollArea';

const ScrollBar = forwardRef<
  ComponentRef<typeof ScrollAreaPrimitive.Scrollbar>,
  ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Scrollbar>
>(function ScrollBar({ className, orientation = 'vertical', ...props }, ref) {
  const isTouch = useContext(ScrollAreaContext);
  const shape = useShape();

  if (isTouch) return null;

  return (
    <ScrollAreaPrimitive.Scrollbar
      ref={ref}
      orientation={orientation}
      data-slot="scroll-area-scrollbar"
      className={cn(
        'group/scrollbar absolute z-20 flex touch-none select-none',
        'opacity-0 transition-opacity duration-120 ease-out delay-160',
        'data-[hovering]:duration-160 data-[scrolling]:duration-160',
        'data-[hovering]:opacity-100 data-[scrolling]:opacity-100',
        'data-[hovering]:delay-0 data-[scrolling]:delay-0',
        orientation === 'vertical' && 'top-0 right-0 h-full w-2.5',
        orientation === 'horizontal' && 'bottom-0 left-0 h-2.5 w-full flex-col',
        className,
      )}
      {...props}
    >
      <ScrollAreaPrimitive.Thumb
        data-slot="scroll-area-thumb"
        className={cn(
          'relative bg-[rgb(var(--overlay)/0.08)] transition-[background-color,width,height] duration-160 ease-in-out',
          'group-hover/scrollbar:bg-[rgb(var(--overlay)/0.12)] active:!bg-[rgb(var(--overlay)/0.16)]',
          shape.bg,
          orientation === 'vertical' &&
            'mx-auto my-1 w-1 -translate-x-0.5 h-[var(--scroll-area-thumb-height)] group-hover/scrollbar:w-1.5',
          orientation === 'horizontal' &&
            'my-auto mx-1 h-1 -translate-y-0.5 w-[var(--scroll-area-thumb-width)] group-hover/scrollbar:h-1.5',
        )}
      />
    </ScrollAreaPrimitive.Scrollbar>
  );
});

ScrollBar.displayName = 'ScrollBar';

export { FluidScrollArea as ScrollArea, ScrollBar };
export type { FluidScrollAreaProps as ScrollAreaProps };
